import { generateAvatarInitials } from '../utils/avatar';
import { 
  CONNECTION_STATUSES, 
  DIRECT_LINE_TOKEN_URL, 
  API_ENDPOINTS, 
  SUCCESS_MESSAGES,
  CONSOLE_MESSAGES,
  JWT_SECRET_FALLBACK,
  ERROR_MESSAGES
} from '../utils/constants';
import {
  maskSensitiveValue,
  isValidHttpHeaderValue,
  inspectTokenCharset
} from '../utils';
import globalConstants from '@/plugins/constants';

export default {
    namespaced: true,
    modules: {},
    state: {
        menu: [
            { product: true, title: globalConstants.ASK_NOA_LABEL, bgcolor: 'primary' },
            {
                title: 'Chat', icon: '/img/icons/ark-agent-icon.svg', href: '/chatbot/home',
            },
        ],
        connectionStatus: null,
        connectionError: null,
        directLineToken: '',
        directLineTenant: '', // Azure AD tenant ID for DirectLine
        tokenContext: null,
        isTokenLoading: false,
        tokenError: null,
        project: null, // used for sending to teams API (future use)
        task: null, // used for sending to teams API (future use)
        filters: null, // used for sending to teams API (future use)
        role: null, // user role in chatbot context
        toolsCatalog: [], // dynamic tools/suggestions from API
        toolsLoading: false,
        toolsError: null,
    },
    mutations: {
        SET_CONNECTION_STATUS(state, status) {
            state.connectionStatus = status;
        },
        SET_CONNECTION_ERROR(state, error) {
            state.connectionError = error;
        },
        SET_DIRECT_LINE_TOKEN(state, { token, userId }) {
            // Decrypt the token using the same method as encryption but in reverse
            function decryptToken(encryptedToken, decryptUserId) {
                if (!encryptedToken) return '';
                
                const envJwtSecret = process.env.VUE_APP_JWT_SECRET_KEY;
                const jwtSecretSource = envJwtSecret ? 'VUE_APP_JWT_SECRET_KEY' : 'fallback';
                const jwtSecret = envJwtSecret || JWT_SECRET_FALLBACK;
                const encryptionKey = `${jwtSecret}_${decryptUserId}`;

                try {
                    // Decode from Base64 (using appropriate method based on environment)
                    let encrypted;
                    if (typeof atob === 'function') {
                        // Browser environment
                        encrypted = atob(encryptedToken);
                    } else if (typeof Buffer !== 'undefined') {
                        // Node.js environment
                        encrypted = Buffer.from(encryptedToken, 'base64').toString('binary');
                    } else {
                        throw new Error('No Base64 decoding method available');
                    }
                    
                    let decrypted = '';
                    
                    // Apply XOR operation to decrypt
                    for (let i = 0; i < encrypted.length; i++) {
                        const charCode = encrypted.charCodeAt(i) ^ encryptionKey.charCodeAt(i % encryptionKey.length);
                        decrypted += String.fromCharCode(charCode);
                    }

                    const charsetInfo = inspectTokenCharset(decrypted);
                    const headerSafe = isValidHttpHeaderValue(decrypted);

                    if (process.env.NODE_ENV !== 'production') {
                        console.debug(CONSOLE_MESSAGES.DECRYPT_TOKEN_DEBUG, {
                            encryptedTokenMasked: maskSensitiveValue(encryptedToken),
                            encryptedTokenLength: encryptedToken.length,
                            base64DecodedLength: encrypted.length,
                            userIdMasked: maskSensitiveValue(String(decryptUserId), { visibleStart: 2, visibleEnd: 2 }),
                            userIdType: typeof decryptUserId,
                            jwtSecretSource,
                            encryptionKeyLength: encryptionKey.length,
                            decryptedTokenMasked: maskSensitiveValue(decrypted),
                            decryptedTokenLength: decrypted.length,
                            isValidHttpHeaderValue: headerSafe,
                            ...charsetInfo
                        });
                    }

                    if (!headerSafe) {
                        console.warn(CONSOLE_MESSAGES.TOKEN_HEADER_INVALID, {
                            hint: 'Likely wrong VUE_APP_JWT_SECRET_KEY or userId used for XOR decrypt',
                            decryptedTokenMasked: maskSensitiveValue(decrypted),
                            ...charsetInfo
                        });
                    }
                    
                    return decrypted;
                } catch (err) {
                    console.error(CONSOLE_MESSAGES.DECRYPT_TOKEN_ERROR, {
                        error: err?.message || err,
                        encryptedTokenMasked: maskSensitiveValue(encryptedToken),
                        userIdMasked: maskSensitiveValue(String(decryptUserId), { visibleStart: 2, visibleEnd: 2 }),
                        jwtSecretSource,
                        jwtSecretMasked: maskSensitiveValue(jwtSecret, { visibleStart: 2, visibleEnd: 2 }),
                        jwtSecretLength: jwtSecret.length,
                        encryptionKeyLength: encryptionKey.length
                    });
                    return '';
                }
            }
            
            // Only attempt to decrypt if we have both a token and a user ID
            if (token && userId) {
                state.directLineToken = decryptToken(token, userId);
            } else {
                console.warn(CONSOLE_MESSAGES.DECRYPT_TOKEN_DEBUG, {
                    skippedDecrypt: true,
                    reason: !token ? 'missing encrypted token' : 'missing userId',
                    hasToken: Boolean(token),
                    hasUserId: Boolean(userId),
                    encryptedTokenMasked: maskSensitiveValue(token),
                    userIdMasked: userId != null ? maskSensitiveValue(String(userId), { visibleStart: 2, visibleEnd: 2 }) : '(empty)'
                });
                state.directLineToken = token || '';
            }
        },
        SET_TOKEN_LOADING(state, isLoading) {
            state.isTokenLoading = isLoading;
        },
        SET_TOKEN_ERROR(state, error) {
            state.tokenError = error;
        },
        SET_PROJECT(state, projectId) {
            state.project = projectId;
        },
        SET_TASK(state, taskId) {
            state.task = taskId;
        },
        SET_FILTERS(state, filters) {
            state.filters = filters;
        },
        SET_ROLE(state, role) {
            state.role = role;
        },
        SET_TOKEN_CONTEXT(state, context) {
            state.tokenContext = context;
        },
        SET_DIRECT_LINE_TOKEN_RAW(state, token) {
            state.directLineToken = token;
        },
        SET_DIRECT_LINE_TENANT(state, tenant) {
            state.directLineTenant = tenant || '';
        },
        SET_TOOLS_CATALOG(state, tools) {
            state.toolsCatalog = tools;
        },
        SET_TOOLS_LOADING(state, isLoading) {
            state.toolsLoading = isLoading;
        },
        SET_TOOLS_ERROR(state, error) {
            state.toolsError = error;
        }
    },
    actions: {
        updateConnectionStatus({ commit }, status) {
            commit('SET_CONNECTION_STATUS', status);
        },
        setConnectionError({ commit }, error) {
            commit('SET_CONNECTION_ERROR', error);
        },
        setProject({ commit }, projectId) {
            commit('SET_PROJECT', projectId);
        },
        setTask({ commit }, taskId) {
            commit('SET_TASK', taskId);
        },
        /**
         * Sync chatbot project/task (and optional role) from the active FC route.
         * Call before opening Ask Noa so DirectLine channelData.params are populated.
         * @param {Object} context
         * @param {{ projectId?: string|null, taskId?: string|null, role?: string|null }} payload
         */
        setContextFromRoute({ commit }, { projectId = null, taskId = null, role = null } = {}) {
            commit('SET_PROJECT', projectId || null);
            commit('SET_TASK', taskId || null);
            if (role) {
                commit('SET_ROLE', role);
            }
            console.log('[ChatbotStore]: setContextFromRoute', { projectId, taskId, role });
        },
        setRole({ commit }, role) {
            commit('SET_ROLE', role);
        },
        async fetchDirectLineToken({ commit, state, rootState, rootGetters }) {
            const currentClient = rootState.client;

            // Check if we have a valid token for the current context
            if (state.directLineToken && state.tokenContext) {
                if (state.tokenContext.client === currentClient) {
                    return { status: 'success', token: state.directLineToken };
                }
            }

            try {
                commit('SET_TOKEN_LOADING', true);
                commit('SET_TOKEN_ERROR', null);
                
                const url = '/v2/chatbot/getEnvConfig';
                const response = await rootGetters.axiosObject.post(url);

                if (response?.data?.status === 'success') {
                    const encryptedDirectLineToken = response.data.data[0].VUE_APP_CHAT_DIRECT_LINE_TOKEN || '';
                    const userId = rootState.user?.id;

                    console.log(CONSOLE_MESSAGES.TOKEN_EXCHANGE_DEBUG, {
                        stage: 'before-decrypt',
                        encryptedTokenMasked: maskSensitiveValue(encryptedDirectLineToken),
                        userIdMasked: userId != null
                          ? maskSensitiveValue(String(userId), { visibleStart: 2, visibleEnd: 2 })
                          : '(empty)',
                        jwtSecretSource: process.env.VUE_APP_JWT_SECRET_KEY
                          ? 'VUE_APP_JWT_SECRET_KEY'
                          : 'fallback',
                        jwtSecretMasked: maskSensitiveValue(
                          process.env.VUE_APP_JWT_SECRET_KEY || JWT_SECRET_FALLBACK,
                          { visibleStart: 2, visibleEnd: 2 }
                        )
                    });

                    // Pass the encrypted token and user ID as an object to the mutation
                    commit('SET_DIRECT_LINE_TOKEN', { 
                        token: encryptedDirectLineToken, 
                        userId
                    });
                    
                    // Store the Azure AD tenant ID for DirectLine
                    commit('SET_DIRECT_LINE_TENANT', response.data.data[0].VUE_APP_CHAT_DIRECT_LINE_TENANT || '');
                    
                    // Get the decrypted token (which might be a Secret or a Token)
                    let currentToken = state.directLineToken;
                    const charsetInfo = inspectTokenCharset(currentToken);
                    const headerSafe = isValidHttpHeaderValue(currentToken);

                    console.log(CONSOLE_MESSAGES.TOKEN_EXCHANGE_DEBUG, {
                        stage: 'after-decrypt',
                        decryptedTokenMasked: maskSensitiveValue(currentToken),
                        isValidHttpHeaderValue: headerSafe,
                        authorizationHeaderMasked: `Bearer ${maskSensitiveValue(currentToken)}`,
                        directLineTokenUrl: DIRECT_LINE_TOKEN_URL,
                        ...charsetInfo
                    });

                    if (!headerSafe) {
                        const errorMessage = ERROR_MESSAGES.FETCH_TOKEN_FAILED;
                        console.error(CONSOLE_MESSAGES.TOKEN_HEADER_INVALID, {
                            stage: 'before-token-exchange',
                            decryptedTokenMasked: maskSensitiveValue(currentToken),
                            hint: 'Aborting Direct Line exchange — Bearer value contains non-printable characters (wrong decrypt key/userId)',
                            ...charsetInfo
                        });
                        commit('SET_TOKEN_ERROR', errorMessage);
                        commit('sbError', errorMessage, { root: true });
                        return { status: 'error', message: errorMessage };
                    }
                    
                    // Try to exchange for a conversation token (in case it's a secret)
                    // This ensures we get a token bound to a conversation
                    try {
                        const genRes = await fetch(DIRECT_LINE_TOKEN_URL, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${currentToken}` }
                        });
                        
                        console.log(CONSOLE_MESSAGES.TOKEN_EXCHANGE_DEBUG, {
                            stage: 'after-token-exchange',
                            httpStatus: genRes.status,
                            ok: genRes.ok,
                            requestTokenMasked: maskSensitiveValue(currentToken)
                        });

                        if (genRes.ok) {
                            const data = await genRes.json();
                            currentToken = data.token;
                            commit('SET_DIRECT_LINE_TOKEN_RAW', currentToken);
                            console.log(CONSOLE_MESSAGES.TOKEN_EXCHANGE_DEBUG, {
                                stage: 'conversation-token-received',
                                conversationTokenMasked: maskSensitiveValue(currentToken)
                            });
                        }
                    } catch (e) {
                        console.warn(CONSOLE_MESSAGES.TOKEN_EXCHANGE_WARN, {
                            error: e?.message || e,
                            requestTokenMasked: maskSensitiveValue(currentToken)
                        });
                    }
                    
                    // Save the context for this token
                    commit('SET_TOKEN_CONTEXT', {
                        client: currentClient,
                        tenant: state.directLineTenant
                    });
                    
                    return { status: 'success', token: currentToken };
                } else {
                    const errorMessage = response?.data?.message || 'Failed to fetch Direct Line token';
                    commit('SET_TOKEN_ERROR', errorMessage);
                    commit('sbError', errorMessage, { root: true });
                    return { status: 'error', message: errorMessage };
                }
            } catch (err) {
                const errorMessage = err?.message || 'Error fetching Direct Line token';
                commit('SET_TOKEN_ERROR', errorMessage);
                commit('sbError', errorMessage, { root: true });
                return { status: 'error', message: errorMessage };
            } finally {
                commit('SET_TOKEN_LOADING', false);
            }
        },
        /**
         * Logout user - handles the logout API call with proper error handling
         * @param {Object} context - Vuex action context
         * @param {Object} context.commit - Vuex commit function
         * @param {Object} context.rootGetters - Root getters for accessing axiosObject
         * @returns {Promise<Object>} - Returns status object {status: 'success'|'error'}
         */
        async logout({ commit, rootGetters }) {
            try {
                const response = await rootGetters.axiosObject.post(API_ENDPOINTS.LOGOUT, {});
                
                if (response.data.status === 'success') {
                    // Clear user state
                    commit('changeLogin', false, { root: true });
                    commit('setUser', null, { root: true });
                    
                    // Show success notification
                    commit('sbSuccess', SUCCESS_MESSAGES.LOGOUT_SUCCESS, { root: true });
                    return { status: 'success' };
                }
                
                return { status: 'error' };
            } catch (error) {
                console.error(CONSOLE_MESSAGES.LOGOUT_ERROR, error);
                // commit('sbError', error.response?.data?.message || 'Logout failed. Please try again.', { root: true });
                return { status: 'error' };
            }
        },
        /**
         * Fetch tools catalog - retrieves available chat tools/suggestions from API
         * @param {Object} context - Vuex action context
         * @param {Object} context.commit - Vuex commit function
         * @param {Object} context.state - Vuex state
         * @param {Object} context.rootGetters - Root getters for accessing axiosObject
         * @returns {Promise<Object>} - Returns status object {status: 'success'|'error', data?: Array}
         */
        async fetchToolsCatalog({ commit, state, rootGetters }) {
            // Return cached data if already loaded
            if (state.toolsCatalog && state.toolsCatalog.length > 0) {
                return { status: 'success', data: state.toolsCatalog };
            }

            try {
                commit('SET_TOOLS_LOADING', true);
                commit('SET_TOOLS_ERROR', null);
                
                const response = await rootGetters.axiosObject.post(API_ENDPOINTS.TOOLS_CATALOG, {});
                
                if (response?.data?.status === 'success' && response?.data?.data?.tools) {
                    // Validate and transform API response to match component expectations
                    const rawTools = response.data.data.tools;
                    
                    // Validate each tool has required fields
                    const validatedTools = rawTools
                        .filter(tool => {
                            // Ensure tool has minimum required fields
                            const hasRequiredFields = tool && 
                                typeof tool.title === 'string' && 
                                typeof tool.prompt === 'string';
                            
                            if (!hasRequiredFields) {
                                console.warn(CONSOLE_MESSAGES.FETCH_TOKEN_ERROR, 'Invalid tool structure:', tool);
                            }
                            
                            return hasRequiredFields;
                        })
                        .map(tool => ({
                            title: tool.title,
                            prompt: tool.prompt,
                            description: tool.description || '',
                            parametersDescription: tool.parametersDescription || [],
                            examples: tool.examples || [],
                            application: String(tool.application || 'common').toLowerCase()
                        }));
                    
                    commit('SET_TOOLS_CATALOG', validatedTools);
                    return { status: 'success', data: validatedTools };
                } else {
                    const errorMessage = response?.data?.message || 'Failed to fetch tools catalog';
                    commit('SET_TOOLS_ERROR', errorMessage);
                    commit('sbError', errorMessage, { root: true });
                    return { status: 'error', message: errorMessage };
                }
            } catch (error) {
                console.error(CONSOLE_MESSAGES.FETCH_TOKEN_ERROR, error);
                const errorMessage = error?.response?.data?.message || 'Error fetching tools catalog';
                commit('SET_TOOLS_ERROR', errorMessage);
                commit('sbError', errorMessage, { root: true });
                return { status: 'error', message: errorMessage };
            } finally {
                commit('SET_TOOLS_LOADING', false);
            }
        }
    },
    getters: {
        isConnected: state => state.connectionStatus === CONNECTION_STATUSES.CONNECTED,
        connectionError: state => state.connectionError,
        directLineTenant: state => state.directLineTenant,
        computedStyleOptions: (state, getters, rootState) => {
            return {
                userAvatarInitials: rootState.user && rootState.user.name
                    ? generateAvatarInitials(rootState.user.name)
                    : ''
            };
        },
        toolsCatalog: state => state.toolsCatalog,
        isToolsLoading: state => state.toolsLoading,
        toolsError: state => state.toolsError
    }
}